/**
 * Family Mode — frontend-only MVP.
 *
 * NOTE: this is an ULTRA-LOW-CREDIT prototype layer. Everything is stored
 * in browser localStorage — no backend schema change, no API calls.
 *
 * Implications (acceptable for the 20-person foundation beta):
 *  - Member list and per-trip member assignments are NOT synced across
 *    devices.
 *  - Clearing browser data wipes assignments (the trip itself survives
 *    on the server; only the "Whose appointment?" label disappears).
 *  - The CRA Line 33099 vs 33199 tax-split is NOT calculated here — this
 *    feature is purely a labelling/filtering convenience while we gather
 *    beta feedback. The full backend Family Mode (with proper Member
 *    records on trips + tax-split logic) is the post-beta upgrade path.
 *
 * Storage keys:
 *  - medeasy_family_members      → JSON array of member name strings
 *  - medeasy_trip_member_assignments → JSON map of { [tripId]: memberName }
 */

const MEMBERS_KEY = "medeasy_family_members";
const ASSIGNMENTS_KEY = "medeasy_trip_member_assignments";
const CHANGE_EVENT = "medeasy:family-members-changed";

/** Returns the current list of family member names, always with at least "Self". */
export function getFamilyMembers() {
  try {
    const raw = localStorage.getItem(MEMBERS_KEY);
    if (!raw) return ["Self"];
    const arr = JSON.parse(raw);
    if (!Array.isArray(arr) || arr.length === 0) return ["Self"];
    // De-dupe + trim, preserve order, guarantee "Self" is present
    const seen = new Set();
    const cleaned = [];
    for (const m of arr) {
      if (typeof m !== "string") continue;
      const t = m.trim();
      if (!t) continue;
      const key = t.toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      cleaned.push(t);
    }
    if (!cleaned.some((m) => m.toLowerCase() === "self")) {
      cleaned.unshift("Self");
    }
    return cleaned.length > 0 ? cleaned : ["Self"];
  } catch {
    return ["Self"];
  }
}

/** Replaces the entire family member list. "Self" is force-included. */
export function setFamilyMembers(members) {
  const cleaned = [];
  const seen = new Set();
  for (const m of members || []) {
    if (typeof m !== "string") continue;
    const t = m.trim();
    if (!t) continue;
    const key = t.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    cleaned.push(t);
  }
  if (!cleaned.some((m) => m.toLowerCase() === "self")) {
    cleaned.unshift("Self");
  }
  if (cleaned.length === 0) cleaned.push("Self");
  localStorage.setItem(MEMBERS_KEY, JSON.stringify(cleaned));
  window.dispatchEvent(new CustomEvent(CHANGE_EVENT));
  return cleaned;
}

/** Adds one member; idempotent. Returns the updated list. */
export function addFamilyMember(name) {
  const current = getFamilyMembers();
  const trimmed = (name || "").trim();
  if (!trimmed) return current;
  if (current.some((m) => m.toLowerCase() === trimmed.toLowerCase())) return current;
  return setFamilyMembers([...current, trimmed]);
}

/** Removes a member by name. "Self" cannot be removed. Returns updated list. */
export function removeFamilyMember(name) {
  if ((name || "").toLowerCase() === "self") return getFamilyMembers();
  const current = getFamilyMembers();
  const next = current.filter((m) => m.toLowerCase() !== (name || "").toLowerCase());
  return setFamilyMembers(next);
}

/** Reads the trip→member assignment map. (Legacy — superseded by the
 * notes-prefix encoding in parseNotesMember / formatNotesWithMember. Kept
 * to avoid orphaning helper imports while we transition; no longer called
 * by the Trip form.) */
function readAssignments() {
  try {
    const raw = localStorage.getItem(ASSIGNMENTS_KEY);
    if (!raw) return {};
    const obj = JSON.parse(raw);
    return obj && typeof obj === "object" ? obj : {};
  } catch {
    return {};
  }
}

/** Returns the member name assigned to a given trip, or "Self" if unset. */
export function getTripMember(tripId) {
  if (!tripId) return "Self";
  const map = readAssignments();
  return map[tripId] || "Self";
}

/** Stores the member assignment for a given trip. */
export function setTripMember(tripId, memberName) {
  if (!tripId) return;
  const map = readAssignments();
  const name = (memberName || "Self").trim() || "Self";
  if (name.toLowerCase() === "self") {
    delete map[tripId];
  } else {
    map[tripId] = name;
  }
  localStorage.setItem(ASSIGNMENTS_KEY, JSON.stringify(map));
}

/**
 * React hook: subscribe a component to family-member-list changes so
 * it re-renders when the Settings page edits the list.
 */
export function subscribeToFamilyChanges(callback) {
  const handler = () => callback(getFamilyMembers());
  window.addEventListener(CHANGE_EVENT, handler);
  // Cross-tab updates fire the storage event natively
  const storageHandler = (e) => {
    if (e.key === MEMBERS_KEY) callback(getFamilyMembers());
  };
  window.addEventListener("storage", storageHandler);
  return () => {
    window.removeEventListener(CHANGE_EVENT, handler);
    window.removeEventListener("storage", storageHandler);
  };
}

// -----------------------------------------------------------------------------
// Notes-prefix encoding — the CURRENT source of truth for per-trip member tags.
// -----------------------------------------------------------------------------
// We piggy-back on the trip's existing `notes` field by stamping the chosen
// family member as `[Member]` at the very start (e.g. `[Mom] Doctor visit`).
// This means the tag rides along with the rest of the trip body — it
// persists across devices, gets backed up with the trip, and survives a
// browser-data wipe. The frontend strips the prefix on load so users only
// see their original prose in the textarea.
//
// Rules of the encoding:
//   - Member equal to "Self" (case-insensitive) writes NO prefix — keeps
//     solo users' notes pristine.
//   - Member names are constrained to 40 chars + no newline + no `]` so
//     the regex match is safe and bounded.
//   - On save we strip any existing leading `[xxx]` before re-prepending
//     so toggling the dropdown can't double-tag.

const MEMBER_PREFIX_RE = /^\[([^\]\n]{1,40})\]\s*/;

/**
 * Parses a notes string and pulls out the member tag if present.
 * Returns { member, cleanNotes }.
 *   parseNotesMember("[Mom] Doctor visit") → { member: "Mom", cleanNotes: "Doctor visit" }
 *   parseNotesMember("Just a note")        → { member: "Self", cleanNotes: "Just a note" }
 *   parseNotesMember("")                   → { member: "Self", cleanNotes: "" }
 */
export function parseNotesMember(notes) {
  const raw = typeof notes === "string" ? notes : "";
  const match = raw.match(MEMBER_PREFIX_RE);
  if (!match) return { member: "Self", cleanNotes: raw };
  const name = (match[1] || "").trim();
  return {
    member: name || "Self",
    cleanNotes: raw.slice(match[0].length),
  };
}

/**
 * Builds the notes string to send to the backend. Member="Self" → no
 * prefix. Any pre-existing `[xxx]` prefix in `notes` is stripped first
 * so toggling the dropdown can't accumulate duplicate tags.
 *   formatNotesWithMember("Mom", "Doctor visit") → "[Mom] Doctor visit"
 *   formatNotesWithMember("Self", "Doctor visit") → "Doctor visit"
 *   formatNotesWithMember("Mom", "[Dad] visit")   → "[Mom] visit"
 */
export function formatNotesWithMember(member, notes) {
  const m = (member || "Self").trim();
  const rawNotes = typeof notes === "string" ? notes : "";
  // Strip any existing leading [bracket] tag — defends against the user
  // typing `[Mom]` directly into the textarea, or against a future save
  // where the dropdown changed.
  const stripped = rawNotes.replace(MEMBER_PREFIX_RE, "");
  if (!m || m.toLowerCase() === "self") return stripped;
  if (!stripped) return `[${m}]`;
  return `[${m}] ${stripped}`;
}
